import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, Target, TrendingUp, DollarSign, Users, Globe, Zap, ArrowUpRight, Activity, BarChart3, ChevronRight, Lock, Eye, Cpu, Layers, AlertTriangle, Clock, Award, Rocket, Server, Radio, Crosshair } from "lucide-react";
import { useAuth } from "@/App";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, RadialBarChart, RadialBar } from "recharts";

const REVENUE_TARGET = 10_000_000;
const TARGET_DATE = new Date("2029-02-01T00:00:00Z");

function useCountdown(target) {
  const [left, setLeft] = useState({});
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const diff = target - now;
      if (diff <= 0) { setLeft({ d: 0, h: 0, m: 0, s: 0 }); return; }
      setLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [target]);
  return left;
}

const REVENUE_PROJECTION = [
  { q: "Q1 2026", mrr: 2400, arr: 28800, users: 340, pat: -120000 },
  { q: "Q2 2026", mrr: 8500, arr: 102000, users: 890, pat: -85000 },
  { q: "Q3 2026", mrr: 24000, arr: 288000, users: 2100, pat: -40000 },
  { q: "Q4 2026", mrr: 65000, arr: 780000, users: 5800, pat: 15000 },
  { q: "Q1 2027", mrr: 120000, arr: 1440000, users: 12000, pat: 180000 },
  { q: "Q2 2027", mrr: 210000, arr: 2520000, users: 22000, pat: 520000 },
  { q: "Q3 2027", mrr: 380000, arr: 4560000, users: 41000, pat: 1200000 },
  { q: "Q4 2027", mrr: 580000, arr: 6960000, users: 68000, pat: 2800000 },
  { q: "Q1 2028", mrr: 820000, arr: 9840000, users: 105000, pat: 4500000 },
  { q: "Q2 2028", mrr: 1100000, arr: 13200000, users: 155000, pat: 6800000 },
  { q: "Q3 2028", mrr: 1450000, arr: 17400000, users: 215000, pat: 9200000 },
  { q: "Q4 2028", mrr: 1850000, arr: 22200000, users: 290000, pat: 10000000 },
];

const MILESTONES = [
  { phase: "PHASE 1", title: "Foundation", target: "$100K ARR", tasks: ["Launch MVP with AI breach scanner", "Stripe + Razorpay payments live", "First 500 paid users", "SOC2 Type I certification"], status: "active", progress: 35 },
  { phase: "PHASE 2", title: "Growth Engine", target: "$1M ARR", tasks: ["ZyberGuard Agentic AI launch", "B2B enterprise contracts", "Agent Marketplace live", "Patent filing for agentic defense"], status: "upcoming", progress: 0 },
  { phase: "PHASE 3", title: "Scale", target: "$10M ARR", tasks: ["Global government contracts", "500+ marketplace agents", "Series A fundraise", "SOC dashboard for enterprises"], status: "future", progress: 0 },
  { phase: "PHASE 4", title: "Dominance", target: "$100M ARR", tasks: ["IPO readiness / Series C", "Acquisition targets", "1M+ enterprise seats", "Global security operations center"], status: "future", progress: 0 },
];

const LIVE_KPI = [
  { label: "Current MRR", value: "$2,400", icon: DollarSign, delta: "+340%", color: "text-emerald-400", bg: "bg-emerald-500/10" },
  { label: "Active Users", value: "18,247", icon: Users, delta: "+28%", color: "text-blue-400", bg: "bg-blue-500/10" },
  { label: "Threats Blocked", value: "1.2M", icon: Shield, delta: "+156%", color: "text-cyan-400", bg: "bg-cyan-500/10" },
  { label: "AI Queries/Day", value: "48,291", icon: Cpu, delta: "+89%", color: "text-amber-400", bg: "bg-amber-500/10" },
  { label: "Uptime", value: "99.97%", icon: Activity, delta: "SLA Met", color: "text-emerald-400", bg: "bg-emerald-500/10" },
  { label: "Countries", value: "87", icon: Globe, delta: "+12 this mo", color: "text-violet-400", bg: "bg-violet-500/10" },
];

const MARKET_SEGMENTS = [
  { name: "SMB", value: 45, fill: "#3b82f6" },
  { name: "Mid-Market", value: 30, fill: "#06b6d4" },
  { name: "Enterprise", value: 15, fill: "#8b5cf6" },
  { name: "Government", value: 10, fill: "#f59e0b" },
];

const fadeIn = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };

function CountdownUnit({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-sm bg-zinc-900/80 border border-cyan-500/20 flex items-center justify-center backdrop-blur-sm">
          <span className="text-3xl sm:text-4xl font-black text-cyan-400 tabular-nums font-mono">{String(value ?? 0).padStart(2, "0")}</span>
        </div>
        <div className="absolute inset-0 rounded-sm border border-cyan-500/10 animate-pulse pointer-events-none" />
      </div>
      <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mt-2">{label}</span>
    </div>
  );
}

function ScanLine() {
  return <div className="absolute left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent animate-[scan-line_4s_linear_infinite] pointer-events-none" />;
}

export default function FoundersCockpitPage() {
  const { user } = useAuth();
  const countdown = useCountdown(TARGET_DATE);
  const [activeTab, setActiveTab] = useState("overview");
  const [terminalLines, setTerminalLines] = useState([]);

  useEffect(() => {
    const lines = [
      "> ZYBERCURE FOUNDERS COCKPIT v2.0",
      "> Initializing secure connection...",
      "> Authentication: VERIFIED",
      `> Operator: ${user?.name || user?.email || "FOUNDER"}`,
      "> Loading mission parameters...",
      "> Target: $10M PAT by FY2029",
      "> Status: MISSION ACTIVE",
      "> All systems operational. Jay Shree Krishna.",
    ];
    let i = 0;
    const iv = setInterval(() => {
      if (i < lines.length) {
        setTerminalLines(prev => [...prev, lines[i]]);
        i++;
      } else clearInterval(iv);
    }, 300);
    return () => clearInterval(iv);
  }, [user]);

  const tabs = [
    { id: "overview", label: "OVERVIEW", icon: Eye },
    { id: "revenue", label: "REVENUE", icon: DollarSign },
    { id: "milestones", label: "MILESTONES", icon: Target },
    { id: "metrics", label: "METRICS", icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-[#060810] text-zinc-100 overflow-hidden relative" data-testid="founders-cockpit">
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(6,182,212,0.05)_0%,_transparent_50%)]" />
        <div className="absolute inset-0 grid-pattern opacity-[0.04]" />
        <ScanLine />
      </div>

      {/* Top Bar */}
      <header className="relative z-20 border-b border-cyan-500/10 bg-[#060810]/90 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="cockpit-logo">
              <Shield className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
              <span className="text-sm font-black tracking-tighter" style={{ fontFamily: "var(--font-heading)" }}>ZYBERCURE</span>
            </Link>
            <span className="text-[10px] tracking-[0.2em] text-cyan-500/60 font-bold border-l border-zinc-800 pl-3 ml-1">FOUNDERS COCKPIT</span>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1 rounded-sm bg-emerald-500/10 border border-emerald-500/20">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] uppercase tracking-wider text-emerald-400 font-bold">SYSTEMS ONLINE</span>
            </div>
            <div className="text-[10px] text-zinc-500 font-mono">{new Date().toISOString().slice(0, 19).replace("T", " ")} UTC</div>
          </div>
        </div>
      </header>

      <div className="relative z-10 max-w-[1600px] mx-auto px-6 py-6">
        <p data-testid="cockpit-demo-disclosure" className="text-sm text-amber-200 border-l-2 border-amber-500 pl-4 mb-6">PLANNING DEMO — all KPIs, revenue figures, uptime, user totals and growth charts below are illustrative assumptions, not measured company performance. This is not a privileged admin console.</p>
        {/* Terminal Boot */}
        <motion.div {...fadeIn} className="mb-6 p-4 bg-zinc-900/50 rounded-sm border border-zinc-800/50 font-mono text-xs overflow-hidden max-h-32" data-testid="cockpit-terminal">
          {terminalLines.map((line, i) => (
            <div key={i} className={`${line.includes("VERIFIED") || line.includes("ACTIVE") || line.includes("Krishna") ? "text-emerald-400" : line.includes("$10M") ? "text-cyan-400" : "text-zinc-500"}`}>{line}</div>
          ))}
          <span className="text-cyan-400 animate-pulse">_</span>
        </motion.div>

        {/* Mission Countdown */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }} className="mb-8" data-testid="mission-countdown">
          <div className="text-center mb-4">
            <span className="text-[10px] tracking-[0.3em] uppercase text-cyan-500/60 font-bold">MISSION COUNTDOWN</span>
            <h2 className="text-xl sm:text-2xl font-black tracking-tighter mt-1" style={{ fontFamily: "var(--font-heading)" }}>
              <span className="text-zinc-300">$10,000,000 PAT</span>
              <span className="text-zinc-600 mx-2">//</span>
              <span className="text-cyan-400">FY2029 TARGET</span>
            </h2>
          </div>
          <div className="flex justify-center gap-3 sm:gap-4">
            <CountdownUnit value={countdown.d} label="Days" />
            <CountdownUnit value={countdown.h} label="Hours" />
            <CountdownUnit value={countdown.m} label="Minutes" />
            <CountdownUnit value={countdown.s} label="Seconds" />
          </div>
          <div className="mt-4 max-w-md mx-auto">
            <div className="flex justify-between text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-1">
              <span>Current: $0 PAT</span>
              <span>Target: $10M PAT</span>
            </div>
            <div className="h-2 bg-zinc-900 rounded-full overflow-hidden border border-zinc-800">
              <motion.div initial={{ width: 0 }} animate={{ width: "2%" }} transition={{ delay: 1, duration: 1.5 }} className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full relative">
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-cyan-400 border-2 border-[#060810] shadow-[0_0_10px_rgba(6,182,212,0.6)]" />
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mb-6 border-b border-zinc-800/50 pb-px overflow-x-auto">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] font-bold transition-all whitespace-nowrap ${activeTab === tab.id ? "text-cyan-400 border-b-2 border-cyan-400 bg-cyan-500/5" : "text-zinc-500 hover:text-zinc-300"}`} data-testid={`tab-${tab.id}`}>
              <tab.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === "overview" && (
            <motion.div key="overview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
              {/* KPIs Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-grid">
                {LIVE_KPI.map((kpi, i) => (
                  <motion.div key={kpi.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className="bg-zinc-900/50 border border-zinc-800/50 rounded-sm p-4 hover:border-zinc-700 transition-all group">
                    <div className="flex items-center justify-between mb-3">
                      <div className={`w-8 h-8 rounded-sm ${kpi.bg} flex items-center justify-center`}>
                        <kpi.icon className={`w-4 h-4 ${kpi.color}`} strokeWidth={1.5} />
                      </div>
                      <span className={`text-[10px] font-bold ${kpi.color}`}>{kpi.delta}</span>
                    </div>
                    <div className={`text-xl font-black tracking-tight ${kpi.color}`} style={{ fontFamily: "var(--font-heading)" }}>{kpi.value}</div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-bold mt-1">{kpi.label}</div>
                  </motion.div>
                ))}
              </div>

              {/* Revenue Chart + Market Segments */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 bg-zinc-900/50 border border-zinc-800/50 rounded-sm p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <span className="text-[10px] tracking-[0.2em] uppercase text-cyan-500/60 font-bold">REVENUE TRAJECTORY</span>
                      <h3 className="text-base font-bold tracking-tight mt-1">ARR Projection to $22.2M</h3>
                    </div>
                    <div className="text-[10px] text-zinc-500 font-mono">FY2026-2028</div>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={REVENUE_PROJECTION}>
                      <defs>
                        <linearGradient id="arrGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#06b6d4" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="q" tick={{ fontSize: 10, fill: "#71717a" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: "#71717a" }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000000).toFixed(1)}M`} />
                      <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 2, fontSize: 11 }} formatter={(v) => [`$${v.toLocaleString()}`, ""]} />
                      <Area type="monotone" dataKey="arr" stroke="#06b6d4" fill="url(#arrGrad)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-sm p-5">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-cyan-500/60 font-bold">TARGET SEGMENTS</span>
                  <h3 className="text-base font-bold tracking-tight mt-1 mb-4">Revenue Mix</h3>
                  <div className="space-y-3">
                    {MARKET_SEGMENTS.map(seg => (
                      <div key={seg.name}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-zinc-400">{seg.name}</span>
                          <span className="font-bold" style={{ color: seg.fill }}>{seg.value}%</span>
                        </div>
                        <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                          <motion.div initial={{ width: 0 }} animate={{ width: `${seg.value}%` }} transition={{ delay: 0.5, duration: 1 }} className="h-full rounded-full" style={{ background: seg.fill }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Strategic Priorities */}
              <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-sm p-5">
                <span className="text-[10px] tracking-[0.2em] uppercase text-cyan-500/60 font-bold">STRATEGIC PRIORITIES — PATH TO $1B</span>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                  {[
                    { icon: Rocket, title: "Product-Led Growth", items: ["Freemium → Pro conversion at 8%+", "Viral breach checker sharing", "Community agent marketplace", "API-first developer adoption"] },
                    { icon: Globe, title: "Global Expansion", items: ["India-first, APAC-second strategy", "Government defense contracts", "EU GDPR compliance tooling", "Middle East cybersec partnerships"] },
                    { icon: Layers, title: "Platform Moats", items: ["ZyberGuard Agentic AI patent", "Proprietary threat intelligence", "Agent execution runtime", "Multi-LLM orchestration layer"] },
                    { icon: Award, title: "Revenue Engines", items: ["SaaS subscriptions (B2C + B2B)", "Enterprise contracts ($10K+ ACV)", "Marketplace commissions (20%)", "Government/defense contracts"] },
                  ].map((priority, i) => (
                    <motion.div key={priority.title} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.1 }} className="p-4 bg-zinc-800/30 rounded-sm border border-zinc-800/50">
                      <div className="flex items-center gap-2 mb-3">
                        <priority.icon className="w-4 h-4 text-cyan-400" strokeWidth={1.5} />
                        <span className="text-sm font-bold tracking-tight">{priority.title}</span>
                      </div>
                      <ul className="space-y-1.5">
                        {priority.items.map(item => (
                          <li key={item} className="text-xs text-zinc-400 flex items-start gap-2">
                            <ChevronRight className="w-3 h-3 text-cyan-500/40 mt-0.5 shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === "revenue" && (
            <motion.div key="revenue" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-sm p-5">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-cyan-500/60 font-bold">MRR GROWTH</span>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={REVENUE_PROJECTION}>
                      <XAxis dataKey="q" tick={{ fontSize: 9, fill: "#71717a" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 9, fill: "#71717a" }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}K`} />
                      <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 2, fontSize: 11 }} />
                      <Bar dataKey="mrr" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-sm p-5">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-cyan-500/60 font-bold">PAT TRAJECTORY</span>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={REVENUE_PROJECTION}>
                      <XAxis dataKey="q" tick={{ fontSize: 9, fill: "#71717a" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 9, fill: "#71717a" }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000000).toFixed(1)}M`} />
                      <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 2, fontSize: 11 }} />
                      <Line type="monotone" dataKey="pat" stroke="#10b981" strokeWidth={2} dot={{ fill: "#10b981", r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-sm p-5">
                <span className="text-[10px] tracking-[0.2em] uppercase text-cyan-500/60 font-bold">USER GROWTH</span>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={REVENUE_PROJECTION}>
                    <defs>
                      <linearGradient id="userGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="q" tick={{ fontSize: 9, fill: "#71717a" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9, fill: "#71717a" }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                    <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 2, fontSize: 11 }} />
                    <Area type="monotone" dataKey="users" stroke="#8b5cf6" fill="url(#userGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          )}

          {activeTab === "milestones" && (
            <motion.div key="milestones" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              {MILESTONES.map((ms, i) => (
                <motion.div key={ms.phase} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.15 }} className={`bg-zinc-900/50 border rounded-sm p-5 relative overflow-hidden ${ms.status === "active" ? "border-cyan-500/30" : "border-zinc-800/50"}`} data-testid={`milestone-${ms.phase}`}>
                  {ms.status === "active" && <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-cyan-500 via-blue-500 to-transparent" />}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="shrink-0">
                      <span className={`text-[10px] tracking-[0.2em] uppercase font-bold ${ms.status === "active" ? "text-cyan-400" : ms.status === "upcoming" ? "text-blue-400" : "text-zinc-600"}`}>{ms.phase}</span>
                      <h3 className="text-lg font-bold tracking-tight mt-0.5" style={{ fontFamily: "var(--font-heading)" }}>{ms.title}</h3>
                      <div className={`text-sm font-bold mt-1 ${ms.status === "active" ? "text-cyan-400" : "text-zinc-500"}`}>{ms.target}</div>
                    </div>
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {ms.tasks.map(task => (
                        <div key={task} className="flex items-center gap-2 text-xs text-zinc-400">
                          <div className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${ms.status === "active" ? "border-cyan-500/30 bg-cyan-500/5" : "border-zinc-700 bg-zinc-800/50"}`}>
                            {ms.progress > 0 && <div className="w-2 h-2 rounded-sm bg-cyan-400" />}
                          </div>
                          {task}
                        </div>
                      ))}
                    </div>
                    <div className="shrink-0 w-20">
                      <div className="text-right">
                        <span className={`text-2xl font-black ${ms.status === "active" ? "text-cyan-400" : "text-zinc-700"}`}>{ms.progress}%</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {activeTab === "metrics" && (
            <motion.div key="metrics" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { title: "Customer Acquisition Cost", value: "$12.40", target: "<$15", status: "on-track", detail: "Organic: 65%, Paid: 35%" },
                  { title: "Lifetime Value (LTV)", value: "$348", target: ">$300", status: "on-track", detail: "Avg. retention: 14 months" },
                  { title: "LTV:CAC Ratio", value: "28:1", target: ">3:1", status: "exceeding", detail: "Industry avg: 3-5:1" },
                  { title: "Monthly Churn", value: "2.1%", target: "<5%", status: "on-track", detail: "Net revenue retention: 112%" },
                  { title: "Free → Paid Conversion", value: "7.8%", target: ">5%", status: "exceeding", detail: "Trial activation: 34%" },
                  { title: "NPS Score", value: "72", target: ">50", status: "exceeding", detail: "Promoters: 78%, Detractors: 6%" },
                  { title: "Avg. Revenue Per User", value: "$27.10", target: ">$25", status: "on-track", detail: "Enterprise: $89, SMB: $19" },
                  { title: "Burn Multiple", value: "1.2x", target: "<2x", status: "on-track", detail: "Efficiency improving QoQ" },
                  { title: "Rule of 40", value: "52", target: ">40", status: "exceeding", detail: "Growth 48% + Margin 4%" },
                ].map((metric, i) => (
                  <motion.div key={metric.title} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }} className="bg-zinc-900/50 border border-zinc-800/50 rounded-sm p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">{metric.title}</span>
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm ${metric.status === "exceeding" ? "text-emerald-400 bg-emerald-500/10" : "text-cyan-400 bg-cyan-500/10"}`}>{metric.status}</span>
                    </div>
                    <div className="text-2xl font-black tracking-tight text-zinc-100" style={{ fontFamily: "var(--font-heading)" }}>{metric.value}</div>
                    <div className="flex justify-between mt-1">
                      <span className="text-[10px] text-zinc-500">Target: {metric.target}</span>
                      <span className="text-[10px] text-zinc-600">{metric.detail}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
